import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check, X } from 'lucide-react';

function getTimeAgo(d) {
  if (!d) return '';
  var diff = Date.now() - new Date(d).getTime();
  var hrs = Math.floor(diff / 3600000);
  if (hrs < 1) return 'Just now';
  if (hrs < 24) return hrs + 'h ago';
  return Math.floor(hrs / 24) + 'd ago';
}

function statusBadge(status) {
  if (status === 'accepted') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (status === 'rejected') return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-amber-50 text-amber-700 border-amber-200';
}

function statusLabel(status) {
  if (status === 'accepted') return 'Accepted';
  if (status === 'rejected') return 'Rejected';
  return 'Pending';
}

export default function SubmCard({ entity, pkField, nameField, onSelect, onApprove, onReject }) {
  var latestSub = entity.submissions && entity.submissions[0];
  var payload = latestSub ? latestSub.raw_payload || {} : {};
  var approval = latestSub ? latestSub.approval_status || 'pending' : 'pending';
  var subId = latestSub ? latestSub.id : null;

  var name = entity.display_name || entity.primary_key;
  var subCount = entity.submission_count || 0;
  var timeStr = getTimeAgo(entity.latest_date);

  var previewFields = [];
  var keys = Object.keys(payload);
  for (var i = 0; i < keys.length && previewFields.length < 3; i++) {
    var k = keys[i];
    if (k !== pkField && k !== nameField && payload[k]) {
      previewFields.push({ key: k, value: String(payload[k]) });
    }
  }

  return (
    <Card
      className="border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
      onClick={function() { onSelect(entity); }}
      data-testid={'entity-card-' + entity.primary_key}
    >
      <CardContent className="pt-5 pb-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="font-semibold text-base truncate">{name}</p>
            <p className="text-xs text-muted-foreground truncate">{pkField}: {entity.primary_key}</p>
          </div>
          <Badge className={'text-xs font-medium border ' + statusBadge(approval)} variant="outline">
            {statusLabel(approval)}
          </Badge>
        </div>

        <PreviewFields fields={previewFields} />
        <PeriodsRow periods={entity.periods} />

        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          {approval === 'pending' && subId ? (
            <div className="flex gap-1.5" onClick={function(e) { e.stopPropagation(); }}>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={function() { onApprove(subId); }} data-testid={'approve-' + entity.primary_key}>
                <Check className="h-3 w-3" /> Accept
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-700 border-red-200 hover:bg-red-50" onClick={function() { onReject(subId); }} data-testid={'reject-' + entity.primary_key}>
                <X className="h-3 w-3" /> Reject
              </Button>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">{subCount} submission{subCount !== 1 ? 's' : ''} &middot; {timeStr}</span>
          )}
          <span className="text-xs text-primary font-medium flex items-center gap-1">
            View <ArrowRight className="h-3 w-3" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function PreviewFields({ fields }) {
  if (!fields || fields.length === 0) return null;
  return (
    <div className="space-y-1">
      {fields.map(function(f) {
        return (
          <div key={f.key} className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground truncate w-1/3">{f.key}</span>
            <span className="text-xs truncate">{f.value}</span>
          </div>
        );
      })}
    </div>
  );
}

function PeriodsRow({ periods }) {
  if (!periods || periods.length === 0) return null;
  return (
    <div className="flex gap-1.5 flex-wrap">
      {periods.map(function(p) {
        return <Badge key={p} variant="outline" className="text-xs font-normal">{p}</Badge>;
      })}
    </div>
  );
}
